import { HelpCircle, Clock, CheckCircle2, XCircle, Check, Ban } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { cn, timeAgo } from '@/lib/utils'
import { isPending, urgencyVariant, statusVariant, decisionTypeLabel } from '@/lib/decision-utils'
import {
  decisionContext,
  acceptConsequence,
  rejectConsequence,
  whyHere,
} from '@/lib/decision-context'
import { WorkProduct } from './work-product'
import type { Decision } from '@/lib/api'

interface DecisionCardProps {
  decision: Decision
  /** Index within its group — drives the alternating row background. */
  index: number
  focused: boolean
  /** True while this card is animating out after an approve/reject. */
  exiting?: boolean
  onFocus: (id: string) => void
  onApprove: (id: string) => void
  onReject: (id: string) => void
  approving: boolean
  rejecting: boolean
  /** When true, a multi-select checkbox is shown (pending cards only). */
  selectable?: boolean
  selected?: boolean
  onToggleSelect?: (id: string) => void
}

interface DecisionOption {
  label: string
  isDefault: boolean
}

/** Narrow the `unknown` options field to a list of labelled choices, if possible. */
function optionList(options: unknown): DecisionOption[] {
  if (!Array.isArray(options)) return []
  const out: DecisionOption[] = []
  for (const o of options) {
    if (o && typeof o === 'object') {
      const obj = o as Record<string, unknown>
      const label = typeof obj.label === 'string' ? obj.label : typeof obj.action === 'string' ? obj.action : null
      if (label) out.push({ label, isDefault: obj.isDefault === true })
    } else if (typeof o === 'string') {
      out.push({ label: o, isDefault: false })
    }
  }
  return out
}

/** Plain-English statement of what happens if Mark does nothing before timeout. */
function timeoutOutcome(decision: Decision): string {
  const def = (decision.safeDefault ?? '').trim().toLowerCase()
  const when = decision.timeoutAt ? ` (${new Date(decision.timeoutAt).toLocaleString()})` : ''
  if (def === 'approve') return `If you do nothing, the agent's recommended action is auto-approved when this times out${when}.`
  if (def === 'reject' || def === 'cancel') return `If you do nothing, this is automatically rejected when it times out${when}.`
  if (def) return `If you do nothing, the safe default "${decision.safeDefault}" is applied on timeout${when}.`
  return 'This will sit here until you act on it — no automatic action is taken.'
}

/** "Approved by mark · 5M AGO" style resolution line for the history tabs. */
function ResolutionLine({ decision, status }: { decision: Decision; status: string }) {
  const approved = status === 'APPROVED'
  const Icon = approved ? CheckCircle2 : XCircle
  const who = decision.resolvedBy === 'mark' ? 'you' : decision.resolvedBy ?? 'the system'
  const verb = approved
    ? decision.resolvedBy && decision.resolvedBy !== 'mark'
      ? 'Auto-resolved'
      : 'Approved'
    : 'Rejected'
  return (
    <div className="mt-3 ml-3 flex items-center gap-1.5">
      <Icon className={cn('h-3.5 w-3.5', approved ? 'text-[#80ffb4]' : 'text-cmd-error')} aria-hidden="true" />
      <span className="font-label text-[0.65rem] tracking-wider text-cmd-outline">
        {verb} by {who} · {timeAgo(decision.resolvedAt)}
        {decision.resolvedAction ? ` · ${decision.resolvedAction.replace(/_/g, ' ')}` : ''}
      </span>
    </div>
  )
}

export function DecisionCard({
  decision,
  index,
  focused,
  exiting,
  onFocus,
  onApprove,
  onReject,
  approving,
  rejecting,
  selectable,
  selected,
  onToggleSelect,
}: DecisionCardProps) {
  const status = (decision.status ?? 'PENDING').toUpperCase()
  const pending = isPending(decision)
  const showCheckbox = selectable && pending && !!onToggleSelect
  const options = optionList(decision.options)
  const ctx = decisionContext(decision)

  const railColor =
    status === 'APPROVED'
      ? 'bg-[#80ffb4]'
      : status === 'REJECTED'
        ? 'bg-cmd-error'
        : urgencyVariant(decision.urgency) === 'critical'
          ? 'bg-cmd-error'
          : 'bg-primary'

  return (
    <div
      id={`decision-${decision.id}`}
      onClick={() => onFocus(decision.id)}
      className={cn(
        'relative flex flex-col p-5 transition-all duration-300',
        index % 2 === 0 ? 'bg-cmd-container' : 'bg-cmd-high',
        focused && 'ring-1 ring-inset ring-primary',
        exiting && 'pointer-events-none -translate-x-2 scale-[0.99] opacity-0',
      )}
    >
      {/* Status / urgency rail */}
      <div className={cn('absolute left-0 top-0 bottom-0 w-0.5', railColor)} />

      <div className="flex items-start justify-between gap-3 pl-3">
        {showCheckbox && (
          <div className="pt-0.5">
            <Checkbox
              checked={!!selected}
              onCheckedChange={() => onToggleSelect!(decision.id)}
              aria-label={`Select decision: ${decision.summary}`}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge variant={urgencyVariant(decision.urgency)}>
              {(decision.urgency ?? 'STANDARD').toUpperCase()}
            </Badge>
            <Badge variant={statusVariant(status)}>{status}</Badge>
            <span className="font-mono text-[0.65rem] text-cmd-outline">{decision.skillName}</span>
          </div>
          <p className="text-sm font-medium text-white/90 mt-2">{decision.summary}</p>
          {ctx.description && (
            <p className="mt-1.5 text-[0.8rem] leading-relaxed text-white/70">{ctx.description}</p>
          )}
          <p className="font-mono text-[0.65rem] text-cmd-outline mt-1.5">
            {decision.decisionType?.replace(/_/g, ' ')} / {timeAgo(decision.createdAt)}
          </p>
        </div>
      </div>

      {/* The work the agent actually produced — drafted email / message / content / facts */}
      <WorkProduct detail={decision.detail} workProduct={ctx.workProduct} />

      {/* The choices the agent surfaced */}
      {options.length > 0 && (
        <div className="mt-3 pl-3 flex flex-wrap gap-1.5">
          {options.map((o) => (
            <span
              key={o.label}
              className="bg-cmd-high px-2 py-0.5 font-mono text-[0.6rem] text-cmd-outline"
            >
              {o.label}
              {o.isDefault && <span className="ml-1 text-primary">· default</span>}
            </span>
          ))}
        </div>
      )}

      {/* What accepting vs rejecting actually does — the heart of the card. Only
          meaningful while still pending. */}
      {pending && (
        <div className="mt-4 ml-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="border-l-2 border-[#80ffb4]/50 bg-cmd-base/40 px-3 py-2">
            <p className="flex items-center gap-1.5 font-label text-[0.55rem] tracking-widest text-[#80ffb4] uppercase">
              <Check className="h-3 w-3" aria-hidden="true" />
              If you accept
            </p>
            <p className="mt-1 text-[0.75rem] leading-relaxed text-white/80">
              {acceptConsequence(decision, ctx)}
            </p>
          </div>
          <div className="border-l-2 border-cmd-error/50 bg-cmd-base/40 px-3 py-2">
            <p className="flex items-center gap-1.5 font-label text-[0.55rem] tracking-widest text-cmd-error uppercase">
              <Ban className="h-3 w-3" aria-hidden="true" />
              If you reject
            </p>
            <p className="mt-1 text-[0.75rem] leading-relaxed text-white/80">
              {rejectConsequence(decision, ctx)}
            </p>
          </div>
        </div>
      )}

      {/* "Why is this here?" context — only meaningful while still pending */}
      {pending && (
        <div className="mt-3 ml-3 border-l border-cmd-outline-variant pl-3">
          <p className="flex items-center gap-1.5 font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
            <HelpCircle className="h-3 w-3" aria-hidden="true" />
            Why is this here?
          </p>
          <p className="mt-1 text-[0.75rem] leading-relaxed text-white/70">
            {whyHere(decision, ctx, decisionTypeLabel(decision.decisionType))}
          </p>
          <p className="mt-1 flex items-start gap-1.5 text-[0.7rem] leading-relaxed text-cmd-outline">
            <Clock className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
            {timeoutOutcome(decision)}
          </p>
        </div>
      )}

      {pending && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pl-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="success"
                disabled={approving}
                onClick={(e) => {
                  e.stopPropagation()
                  onApprove(decision.id)
                }}
                className="font-label text-xs tracking-wider"
              >
                APPROVE
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {acceptConsequence(decision, ctx)}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="destructive"
                disabled={rejecting}
                onClick={(e) => {
                  e.stopPropagation()
                  onReject(decision.id)
                }}
                className="font-label text-xs tracking-wider"
              >
                REJECT
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {rejectConsequence(decision, ctx)}
            </TooltipContent>
          </Tooltip>

          <span className="font-label text-[0.65rem] text-cmd-outline-variant">
            Approve = do it now · Reject = cancel it
          </span>
        </div>
      )}

      {/* Resolution outcome — history tabs (Accepted / Rejected) */}
      {!pending && (status === 'APPROVED' || status === 'REJECTED') && (
        <ResolutionLine decision={decision} status={status} />
      )}

      {decision.resolutionNote && (
        <p className="mt-2 pl-3 font-mono text-[0.65rem] text-cmd-outline">
          NOTE: {decision.resolutionNote}
        </p>
      )}
    </div>
  )
}
